import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Building2 } from "lucide-react";

import { fetchPublishers } from "../api/issues";
import { Spinner } from "../components/Spinner";
import { shortDate } from "../lib/format";

export function Browse() {
  const q = useQuery({ queryKey: ["publishers"], queryFn: () => fetchPublishers() });

  return (
    <div className="flex flex-col gap-8 animate-fade-up">
      <header className="rule-double pt-6">
        <div className="np-label mb-2">Section I · The Wire</div>
        <h1 className="font-display font-extrabold tracking-tight leading-[0.94]"
            style={{ fontSize: "clamp(46px, 6vw, 80px)" }}>
          Publishers
        </h1>
        <p className="font-display italic text-[20px] text-[var(--fg-muted)] mt-3 max-w-prose">
          Every channel the crawler covers. Pick one to see its city editions.
        </p>
      </header>

      {q.isLoading && (
        <div className="py-12 flex justify-center"><Spinner label="Loading publishers" /></div>
      )}

      {q.isError && (
        <div className="np-banner">
          <span className="font-mono text-[10px] uppercase tracking-folio mr-2 text-[var(--accent)]">Error</span>
          Failed to load publishers. Try refreshing.
        </div>
      )}

      {q.data && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {q.data.map((p) => (
            <Link
              key={p.channel_id}
              to={`/browse/${p.channel_id}`}
              className="np-card p-6 group hover:shadow-plate transition-shadow flex flex-col gap-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 size={12} className="text-[var(--fg-faint)]" />
                    <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
                      Channel · {p.channel_id}
                    </span>
                  </div>
                  <h3 className="font-display text-[26px] leading-tight font-semibold truncate">
                    {p.channel_name}
                  </h3>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-[var(--fg-faint)] shrink-0 transition-all group-hover:text-[var(--accent)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </div>

              <div className="np-divider" />

              <dl className="grid grid-cols-3 gap-2">
                <Mini label="Editions" value={p.edition_count.toString()} />
                <Mini label="Crawled"  value={p.crawlable_count.toString()} />
                <Mini label="Today"
                      value={p.today_issue_count.toString()}
                      accent={p.today_issue_count > 0} />
              </dl>

              <div className="flex items-baseline justify-between font-mono text-[11px] text-[var(--fg-faint)] mt-1">
                <span>Latest: {p.latest_business_date ? shortDate(p.latest_business_date) : "—"}</span>
                {p.latest_page_count !== null && (
                  <span>{p.latest_page_count} pages</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <dt className="font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]">
        {label}
      </dt>
      <dd
        className="font-display text-2xl tabular-nums leading-none mt-0.5"
        style={accent ? { color: "var(--accent)" } : undefined}
      >
        {value}
      </dd>
    </div>
  );
}
